import { useCallback, useEffect, useRef, VoidFunctionComponent } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { Icon } from 'components/elements';
import { getSeverityIcon } from 'lib/styles';
import {
  NotificationAction,
  NotificationBody,
  NotificationBorder,
  NotificationButton,
  NotificationCaption,
  NotificationContent,
  NotificationIcon,
  NotificationMessage,
  StyledNotification,
} from './Notification.styled';
import { NotificationProps } from './types';

export const Notification: VoidFunctionComponent<NotificationProps> = props => {
  const { data, in: visible, onClose } = props;

  // Extract notification options
  const { caption, duration = 5000, id, message, severity } = data;

  // Reference to the toast element
  const nodeRef = useRef<Nullable<HTMLDivElement>>(null);

  // System theme required to extract the animation duration
  const theme = useTheme();

  /**
   * Handle clicks on the close button
   */
  const handleActionClick = useCallback(() => onClose(id), [id, onClose]);

  // Start the timer when the notification component mounts
  useEffect(() => {
    const timeout = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CSSTransition
      in={visible}
      key={id}
      mountOnEnter={true}
      nodeRef={nodeRef}
      timeout={parseInt(theme.transition.medium)}
      unmountOnExit={true}
    >
      <StyledNotification ref={nodeRef}>
        <NotificationBorder $severity={severity} />
        <NotificationContent>
          <NotificationIcon $severity={severity}>
            <Icon name={getSeverityIcon(severity)} />
          </NotificationIcon>
          <NotificationBody>
            <NotificationCaption>{caption}</NotificationCaption>
            <NotificationMessage>{message}</NotificationMessage>
          </NotificationBody>
          <NotificationAction onClick={handleActionClick}>
            <NotificationButton icon="close" />
          </NotificationAction>
        </NotificationContent>
      </StyledNotification>
    </CSSTransition>
  );
};
